import axios, { AxiosError } from 'axios';
import { auth } from '../firebase';
import { backendAPI } from './products';
import type { InventorySummary, InventoryTransaction } from '../types/commerce';

interface ApiError {
  message?: string;
  error?: string;
}

export type InventoryAdjustPayload = {
  mode: 'increase' | 'decrease' | 'set';
  quantity: number;
  variantId?: number | null;
  reason?: string;
  notes?: string;
};

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not logged in');
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

function mapInventoryError(error: unknown, fallback: string): Error {
  const axiosError = error as AxiosError<ApiError>;
  const message =
    axiosError?.response?.data?.error ||
    axiosError?.response?.data?.message ||
    fallback;
  return new Error(message);
}

export const get_product_inventory = async (
  productId: number,
  variantId?: number | null
): Promise<InventorySummary> => {
  try {
    const response = await backendAPI.get(`/inventory/products/${productId}`, {
      params: variantId ? { variantId } : undefined,
      headers: await authHeaders(),
    });
    return response.data?.summary;
  } catch (error) {
    throw mapInventoryError(error, 'Failed to fetch inventory summary');
  }
};

export const list_product_inventory_transactions = async (
  productId: number,
  options?: { variantId?: number | null; limit?: number; offset?: number }
): Promise<{ total: number; transactions: InventoryTransaction[] }> => {
  try {
    const response = await backendAPI.get(
      `/inventory/products/${productId}/transactions`,
      {
        params: {
          variantId: options?.variantId ?? undefined,
          limit: options?.limit,
          offset: options?.offset,
        },
        headers: await authHeaders(),
      }
    );
    return {
      total: response.data?.total ?? 0,
      transactions: response.data?.transactions ?? [],
    };
  } catch (error) {
    throw mapInventoryError(error, 'Failed to fetch inventory history');
  }
};

export const adjust_product_inventory = async (
  productId: number,
  payload: InventoryAdjustPayload
): Promise<{ transaction: InventoryTransaction | null; summary: InventorySummary }> => {
  try {
    const response = await backendAPI.post(
      `/inventory/products/${productId}/adjust`,
      payload,
      { headers: await authHeaders() }
    );
    return {
      transaction: response.data?.transaction ?? null,
      summary: response.data?.summary,
    };
  } catch (error) {
    throw mapInventoryError(error, 'Failed to adjust inventory');
  }
};

export const reconcile_product_inventory = async (
  productId: number
): Promise<InventorySummary> => {
  try {
    const response = await backendAPI.post(
      `/inventory/products/${productId}/reconcile`,
      {},
      { headers: await authHeaders() }
    );
    return response.data?.summary;
  } catch (error) {
    throw mapInventoryError(error, 'Failed to reconcile inventory');
  }
};
